import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { BackButton, FormField, FormGrid, FormSection, InlineFeedback, PrimaryButton } from 'components/forms';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import moment, { type Moment } from 'moment';
import type React from 'react';
import { type FC, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateTicketMutation, useUploadFileToTicketMutation } from 'services/api/tickets.api';
import {
  type CreateTicketRequest,
  SELECTABLE_TICKET_MOTIVES,
  TICKET_MOTIVE_LABELS,
  TICKET_URGENCIES,
  TICKET_URGENCY_LABELS,
  type TicketMotive,
  type TicketUrgency,
} from 'services/types/ticket';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  STATUS_GOOD,
  STATUS_WARN,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

const FONT = 'Jost, sans-serif';

const glassPaperSx = {
  backdropFilter: GLASS_BLUR,
  backgroundColor: GLASS_BACKGROUND,
  boxShadow: SHADOW,
  borderRadius: RADIUS_CARD,
  border: `1px solid ${alpha(NAVY, 0.06)}`,
} as const;

const glassBackdropSx = {
  backdropFilter: 'blur(10px)',
  backgroundColor: alpha(NAVY, 0.25),
} as const;

const chipSx = {
  borderRadius: RADIUS_ITEM,
  backgroundColor: alpha(NAVY, 0.06),
  color: NAVY,
  fontFamily: FONT,
  fontWeight: 500,
} as const;

const multiSelectSx = {
  width: '100%',
  '&.MuiInputBase-root, &.MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiOutlinedInput-root': {
    borderRadius: RADIUS_ITEM,
    bgcolor: SURFACE_CONTAINER_LOWEST,
    boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.08)}`,
    transition: 'box-shadow 0.18s ease',
    fontFamily: FONT,
    fontSize: '0.9375rem',
    '& fieldset': { border: 'none' },
    '&:hover': { boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.18)}` },
    '&.Mui-focused': {
      boxShadow: `inset 0 0 0 1.5px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.25)}`,
    },
    '&.Mui-disabled': {
      boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.06)}`,
      bgcolor: SURFACE_CONTAINER_LOWEST,
    },
  },
  '& .MuiSelect-select': { py: 1.25, px: 1.75, color: NAVY, minHeight: '24px' },
} as const;

interface FieldShellProps {
  label: string;
  required?: boolean;
  optional?: boolean;
  error?: string | null;
  hint?: ReactNode;
  children: ReactNode;
}

const FieldShell: FC<FieldShellProps> = ({ label, required, optional, error, hint, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
    <Typography
      component="label"
      sx={{
        fontFamily: FONT,
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: alpha(NAVY, 0.7),
        mb: 0.875,
        letterSpacing: '0.005em',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {label}
      {required && (
        <Typography component="span" sx={{ color: '#E7233B', fontWeight: 600, ml: 0.25 }} aria-hidden>
          *
        </Typography>
      )}
      {optional && !required && (
        <Typography component="span" sx={{ color: alpha(NAVY, 0.45), fontWeight: 400, fontSize: '0.75rem', ml: 0.5 }}>
          (opcional)
        </Typography>
      )}
    </Typography>
    {children}
    {(error || hint) && (
      <Typography
        sx={{
          mt: 0.625,
          fontFamily: FONT,
          fontSize: '0.75rem',
          color: error ? '#E7233B' : alpha(NAVY, 0.55),
          lineHeight: 1.45,
        }}
      >
        {error || hint}
      </Typography>
    )}
  </Box>
);

interface CreateTicketDialogProps {
  open: boolean;
  onClose: () => void;
  clientId?: number;
  businessUnitId?: number;
  isAdmin?: boolean;
  onSuccess?: (ticketId: number) => void;
}

const urgencyDotColor = (r: TicketUrgency): string =>
  r === 'HIGH' ? STATUS_WARN : r === 'MEDIUM' ? '#A8810A' : `${NAVY}99`;

const CreateTicketDialog: React.FC<CreateTicketDialogProps> = ({
  open,
  onClose,
  clientId = undefined,
  businessUnitId: initialBusinessUnitId,
  isAdmin = false,
  onSuccess = undefined,
}) => {
  const navigate = useNavigate();
  const [createTicket] = useCreateTicketMutation();
  const [uploadFile] = useUploadFileToTicketMutation();
  const { selectedBusinessUnitId: contextBusinessUnitId } = useSelectedBusinessUnit();
  const { businessUnits, getBusinessUnitsByCompany, companyNames } = useBusinessUnits();

  const getEffectiveBusinessUnitId = (): number | undefined => {
    if (initialBusinessUnitId && initialBusinessUnitId > 0) return initialBusinessUnitId;
    if (contextBusinessUnitId && contextBusinessUnitId > 0) return contextBusinessUnitId;
    return undefined;
  };
  const effectiveBusinessUnitId = getEffectiveBusinessUnitId();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState<number | ''>(effectiveBusinessUnitId ?? '');
  const [motive, setMotive] = useState<TicketMotive | ''>('');
  const [urgency, setUrgency] = useState<TicketUrgency | ''>('');
  const [deadlineDate, setDeadlineDate] = useState<Moment | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdTicketId, setCreatedTicketId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [buFilter, setBuFilter] = useState('');
  const buFilterRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setSelectedBusinessUnitId(effectiveBusinessUnitId ?? '');
    }
  }, [open, effectiveBusinessUnitId]);

  const availableBusinessUnits = businessUnits ?? [];
  const sortedCompanyNames = [...companyNames].sort((a, b) => a.localeCompare(b));
  const selectorLocked = !!(initialBusinessUnitId && initialBusinessUnitId > 0);

  const normalizedBuFilter = buFilter.trim().toLowerCase();
  const filteredCompanyNames = useMemo(
    () =>
      sortedCompanyNames.filter(
        (company) =>
          company.toLowerCase().includes(normalizedBuFilter) ||
          getBusinessUnitsByCompany(company).some((bu) => bu.name.toLowerCase().includes(normalizedBuFilter))
      ),
    [sortedCompanyNames, getBusinessUnitsByCompany, normalizedBuFilter]
  );
  const filteredFlatBusinessUnits = useMemo(
    () =>
      [...availableBusinessUnits]
        .filter((bu) => bu.name.toLowerCase().includes(normalizedBuFilter))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [availableBusinessUnits, normalizedBuFilter]
  );

  const isTitleValid = title.trim().length >= 10;
  const isDescriptionValid = description.trim().length >= 50;
  const isBusinessUnitValid = isAdmin || (typeof selectedBusinessUnitId === 'number' && selectedBusinessUnitId > 0);
  const isMotiveValid = motive !== '';
  const isUrgencyValid = urgency !== '';
  const isDeadlineValid = !deadlineDate || deadlineDate.isValid();
  const isValid =
    isTitleValid && isDescriptionValid && isBusinessUnitValid && isMotiveValid && isUrgencyValid && isDeadlineValid;

  const handleDescriptionPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length > 0) {
      setAttachedFiles((prev) => [...prev, ...files]);
      setError(null);
      event.preventDefault();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setAttachedFiles((prev) => [...prev, ...files]);
    setError(null);
  };

  const removeFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFilesToTicket = async (ticketId: number) => {
    if (attachedFiles.length === 0) return;

    setIsUploadingFiles(true);
    try {
      for (let i = 0; i < attachedFiles.length; i += 1) {
        await uploadFile({ ticketId, file: attachedFiles[i], showToUser: true }).unwrap();
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Error al subir algunos archivos');
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!isValid) return;

    setIsCreating(true);
    setError(null);

    try {
      const ticketData: CreateTicketRequest = {
        title: title.trim(),
        description: description.trim(),
        motive: motive as TicketMotive,
        urgency: urgency as TicketUrgency,
        ...(deadlineDate ? { deadlineDate: deadlineDate.toISOString() } : {}),
        ...(clientId && { clientId }),
        ...(typeof selectedBusinessUnitId === 'number' && selectedBusinessUnitId > 0
          ? { businessUnitId: selectedBusinessUnitId }
          : {}),
      };

      const result = await createTicket(ticketData).unwrap();

      setCreatedTicketId(result.id);

      if (attachedFiles.length > 0) {
        await uploadFilesToTicket(result.id);
      }

      setShowSuccess(true);

      if (onSuccess) {
        onSuccess(result.id);
      }
    } catch (err) {
      setError('Error al crear el ticket. Por favor, intenta nuevamente.');
      console.error('Error creating ticket:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setTitle('');
      setDescription('');
      setSelectedBusinessUnitId(effectiveBusinessUnitId ?? '');
      setMotive('');
      setUrgency('');
      setDeadlineDate(null);
      setError(null);
      setShowSuccess(false);
      setCreatedTicketId(null);
      setAttachedFiles([]);
      onClose();
    }
  };

  const handleViewTicket = () => {
    if (createdTicketId) {
      navigate(`/tickets/${createdTicketId}`);
    }
  };

  const titleHint = `Resume el problema en una frase. Mínimo 10 caracteres${title ? ` (${title.trim().length}/10)` : ''}`;
  const descriptionHint = `Incluye todo lo necesario para entender el problema. Mínimo 50 caracteres${description ? ` (${description.trim().length}/50)` : ''}`;

  if (showSuccess) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: glassPaperSx }, backdrop: { sx: glassBackdropSx } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 3, pb: 1.5, px: 3 }}>
          <CheckCircleIcon sx={{ color: STATUS_GOOD, fontSize: '1.6rem', flexShrink: 0 }} />
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.25rem', color: NAVY, flex: 1 }}>
            Ticket creado
          </Typography>
          <IconButton size="small" onClick={handleClose} sx={{ color: `${NAVY}80` }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.95rem', color: alpha(NAVY, 0.75), mb: 1 }}>
            El ticket ha sido creado exitosamente.
            {attachedFiles.length > 0 && ' Los archivos han sido subidos.'}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: alpha(NAVY, 0.55) }}>
            ¿Qué te gustaría hacer ahora?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Stack direction="row" spacing={1.5} sx={{ width: '100%', justifyContent: 'flex-end' }}>
            <BackButton onClick={handleClose}>Cerrar</BackButton>
            <PrimaryButton onClick={handleViewTicket}>Ver Ticket</PrimaryButton>
          </Stack>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: glassPaperSx }, backdrop: { sx: glassBackdropSx } }}
    >
      <DialogTitle
        id="create-ticket-dialog-title"
        sx={{
          color: NAVY,
          fontWeight: 700,
          fontFamily: FONT,
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>Crear nuevo ticket</Box>
        <IconButton size="small" onClick={handleClose} disabled={isCreating} sx={{ color: alpha(NAVY, 0.5) }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ fontFamily: FONT }}>
        {error && (
          <InlineFeedback severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </InlineFeedback>
        )}

        <FormSection eyebrow="Problema" title="¿Qué pasó?" dense>
          <FormGrid columns={1} gap={2}>
            <FormField
              autoFocus
              label="Título"
              placeholder="Ej. No puedo exportar los leads a CSV"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={title && !isTitleValid ? 'Necesitamos al menos 10 caracteres' : undefined}
              hint={titleHint}
              disabled={isCreating}
              required
            />
            <FormField
              label="Descripción"
              placeholder="Pasos para reproducirlo, qué esperabas, qué pasó en su lugar..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={handleDescriptionPaste}
              error={description && !isDescriptionValid ? 'Necesitamos al menos 50 caracteres' : undefined}
              hint={descriptionHint}
              disabled={isCreating}
              required
              multiline
              rows={4}
            />
          </FormGrid>
        </FormSection>

        <FormSection eyebrow="Clasificación" title="Ayúdanos a priorizar" dense>
          <FormGrid columns={2} gap={2}>
            <FieldShell label="Motivo" required>
              <FormControl fullWidth disabled={isCreating}>
                <Select
                  value={motive}
                  onChange={(event: SelectChangeEvent<string>) => setMotive(event.target.value as TicketMotive)}
                  displayEmpty
                  sx={multiSelectSx}
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Typography sx={{ color: alpha(NAVY, 0.35), fontFamily: FONT, fontSize: '0.9375rem' }}>
                          Selecciona un motivo
                        </Typography>
                      );
                    }
                    return TICKET_MOTIVE_LABELS[value as TicketMotive];
                  }}
                >
                  {SELECTABLE_TICKET_MOTIVES.map((m) => (
                    <MenuItem key={m} value={m} sx={{ fontFamily: FONT }}>
                      {TICKET_MOTIVE_LABELS[m]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FieldShell>

            <FieldShell label="Relevancia" required>
              <FormControl fullWidth disabled={isCreating}>
                <Select
                  value={urgency}
                  onChange={(event: SelectChangeEvent<string>) => setUrgency(event.target.value as TicketUrgency)}
                  displayEmpty
                  sx={multiSelectSx}
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Typography sx={{ color: alpha(NAVY, 0.35), fontFamily: FONT, fontSize: '0.9375rem' }}>
                          Selecciona la relevancia
                        </Typography>
                      );
                    }
                    const r = value as TicketUrgency;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: urgencyDotColor(r) }} />
                        <Typography sx={{ fontFamily: FONT, color: NAVY, fontSize: '0.9375rem' }}>
                          {TICKET_URGENCY_LABELS[r]}
                        </Typography>
                      </Box>
                    );
                  }}
                >
                  {TICKET_URGENCIES.map((r) => (
                    <MenuItem key={r} value={r} sx={{ fontFamily: FONT }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: urgencyDotColor(r) }} />
                        {TICKET_URGENCY_LABELS[r]}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FieldShell>

            <FieldShell label="Fecha tope estimada de solución" optional hint="Cuándo necesitas que esté resuelto">
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  value={deadlineDate}
                  onChange={(newValue) => setDeadlineDate(newValue)}
                  minDate={moment().startOf('day')}
                  disabled={isCreating}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      sx: {
                        '& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root, & .MuiOutlinedInput-root': {
                          borderRadius: RADIUS_ITEM,
                          bgcolor: `${SURFACE_CONTAINER_LOWEST} !important`,
                          boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.08)}`,
                          transition: 'box-shadow 0.18s ease',
                          fontFamily: FONT,
                          fontSize: '0.9375rem',
                          '& fieldset, & .MuiPickersOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover': {
                            boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.18)}`,
                          },
                          '&.Mui-focused': {
                            boxShadow: `inset 0 0 0 1.5px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.25)}`,
                          },
                          '&.Mui-disabled': {
                            boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.06)}`,
                          },
                        },
                        '& .MuiPickersInputBase-sectionsContainer, & .MuiOutlinedInput-input': {
                          py: 1.25,
                          px: 1.75,
                          color: NAVY,
                        },
                      },
                    },
                    field: { clearable: true },
                  }}
                />
              </LocalizationProvider>
            </FieldShell>
          </FormGrid>
        </FormSection>

        <FormSection eyebrow="Contexto" title="¿Dónde y con qué?" dense>
          <FormGrid columns={1} gap={2}>
            {availableBusinessUnits.length > 0 && (
              <FieldShell
                label="Unidad de negocio"
                required={!isAdmin}
                optional={isAdmin}
                hint={selectorLocked ? 'Definida por el contexto desde donde abriste el ticket.' : undefined}
              >
                <FormControl fullWidth disabled={isCreating || selectorLocked}>
                  <Select
                    value={selectedBusinessUnitId === '' ? '' : String(selectedBusinessUnitId)}
                    onChange={(event: SelectChangeEvent<string>) => {
                      const raw = event.target.value;
                      setSelectedBusinessUnitId(raw === '' ? '' : Number(raw));
                    }}
                    onOpen={() => {
                      setBuFilter('');
                      setTimeout(() => buFilterRef.current?.focus(), 0);
                    }}
                    onClose={() => setBuFilter('')}
                    displayEmpty
                    sx={multiSelectSx}
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Typography sx={{ color: alpha(NAVY, 0.35), fontFamily: FONT, fontSize: '0.9375rem' }}>
                            {isAdmin ? 'Sin unidad de negocio' : 'Selecciona una unidad'}
                          </Typography>
                        );
                      }
                      const bu = availableBusinessUnits.find((b) => String(b.id) === value);
                      if (!bu) return '';
                      return bu.company?.name ? `${bu.company.name} / ${bu.name}` : bu.name;
                    }}
                  >
                    <ListSubheader
                      sx={{ px: 1.5, pt: 1, pb: 0.5, backgroundColor: 'transparent', lineHeight: 'unset' }}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <InputBase
                        inputRef={buFilterRef}
                        placeholder="Buscar empresa o unidad..."
                        value={buFilter}
                        onChange={(e) => setBuFilter(e.target.value)}
                        fullWidth
                        startAdornment={
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 16, color: alpha(NAVY, 0.4) }} />
                          </InputAdornment>
                        }
                        sx={{
                          fontFamily: FONT,
                          fontSize: '0.8rem',
                          color: NAVY,
                          backgroundColor: alpha(NAVY, 0.04),
                          borderRadius: RADIUS_ITEM,
                          px: 1,
                          py: 0.5,
                          '& input': { py: 0 },
                          '& input::placeholder': { color: alpha(NAVY, 0.4), opacity: 1 },
                        }}
                      />
                    </ListSubheader>
                    {isAdmin && !normalizedBuFilter && (
                      <MenuItem value="" sx={{ fontFamily: FONT }}>
                        Sin unidad de negocio
                      </MenuItem>
                    )}
                    {isAdmin
                      ? filteredCompanyNames.flatMap((company) => [
                          <ListSubheader
                            key={`company-${company}`}
                            sx={{
                              fontFamily: FONT,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              color: alpha(NAVY, 0.5),
                              backgroundColor: alpha(NAVY, 0.02),
                              lineHeight: '32px',
                            }}
                          >
                            {company}
                          </ListSubheader>,
                          ...getBusinessUnitsByCompany(company)
                            .slice()
                            .filter(
                              (bu) =>
                                bu.name.toLowerCase().includes(normalizedBuFilter) ||
                                company.toLowerCase().includes(normalizedBuFilter)
                            )
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((bu) => (
                              <MenuItem key={bu.id} value={String(bu.id)} sx={{ pl: 3, fontFamily: FONT }}>
                                {bu.name}
                              </MenuItem>
                            )),
                        ])
                      : filteredFlatBusinessUnits.map((bu) => (
                          <MenuItem key={bu.id} value={String(bu.id)} sx={{ fontFamily: FONT }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: NAVY, lineHeight: 1.2 }}>
                                {bu.name}
                              </Typography>
                              {bu.company?.name && (
                                <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: alpha(NAVY, 0.55) }}>
                                  {bu.company.name}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              </FieldShell>
            )}

            <FieldShell
              label={`Archivos adjuntos (${attachedFiles.length})`}
              optional
              hint="Imágenes, PDFs, documentos, videos o capturas pegadas en la descripción."
            >
              <Box
                sx={{
                  bgcolor: SURFACE_CONTAINER_LOWEST,
                  borderRadius: RADIUS_ITEM,
                  boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.08)}`,
                  p: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: attachedFiles.length > 0 ? 1.5 : 0 }}>
                  <IconButton
                    component="label"
                    disabled={isCreating}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: RADIUS_ITEM,
                      bgcolor: alpha(NAVY, 0.06),
                      color: NAVY,
                      '&:hover': { bgcolor: alpha(NAVY, 0.12) },
                    }}
                  >
                    <AttachFileIcon fontSize="small" />
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xls,.xlsx,video/*"
                      onChange={handleFileSelect}
                      multiple
                      disabled={isCreating}
                    />
                  </IconButton>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', color: alpha(NAVY, 0.55) }}>
                    Arrastra archivos aquí o haz clic para seleccionar
                  </Typography>
                </Box>

                {attachedFiles.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {attachedFiles.map((file, index) => (
                      <Chip
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        label={file.name}
                        onDelete={() => removeFile(index)}
                        size="small"
                        sx={chipSx}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </FieldShell>

            {!clientId && (
              <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: alpha(NAVY, 0.55), lineHeight: 1.5 }}>
                Si es sobre un cliente en específico, incluye el id del cliente en la descripción o crea el ticket desde
                la vista de ese cliente.
              </Typography>
            )}
          </FormGrid>
        </FormSection>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
        <Stack direction="row" spacing={1.5} sx={{ width: '100%', justifyContent: 'flex-end' }}>
          <BackButton onClick={handleClose} disabled={isCreating}>
            Cancelar
          </BackButton>
          <PrimaryButton
            onClick={() => {
              handleCreateTicket().catch((err) => {
                console.error('Error creating ticket:', err);
              });
            }}
            disabled={!isValid || isCreating || isUploadingFiles}
          >
            {isCreating || isUploadingFiles ? 'Creando…' : 'Crear ticket'}
          </PrimaryButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTicketDialog;
